
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Icon, type IconName } from '@/components/ui/Icon'
import { cx } from '@/lib/ui'
import { useUiMode } from '@/components/workspace/UiModeProvider'

/** `advancedOnly` sections stay out of the way until somebody asks for them. */
const ITEMS: { segment: string; label: string; icon: IconName; advancedOnly?: true }[] = [
  { segment: 'projects', label: 'Projects', icon: 'projects' },
  { segment: 'library', label: 'Equipment', icon: 'equipment' },
  // Asset tags and serial numbers are a rental-company concern, not a
  // meeting-room one.
  { segment: 'inventory', label: 'Inventory', icon: 'inventory', advancedOnly: true },
  { segment: 'power', label: 'Power', icon: 'plug' },
  { segment: 'settings', label: 'Settings', icon: 'settings' },
]

export function RailNav({ base }: { base: string }) {
  const pathname = usePathname()
  const { advanced } = useUiMode()
  return (
    <nav aria-label="Workspace" className="flex items-center gap-1 sm:flex-col">
      {ITEMS.filter((item) => advanced || !item.advancedOnly).map((item) => {
        const href = `${base}/${item.segment}`
        const active = pathname === href || pathname.startsWith(`${href}/`)
        return (
          <Link
            key={item.segment}
            href={href}
            aria-current={active ? 'page' : undefined}
            className={cx(
              'group relative grid h-11 w-11 place-items-center rounded-2xl transition-all duration-200',
              active
                ? 'bg-[var(--ink)] text-on-ink shadow-[var(--shadow-btn)]'
                : 'text-muted hover:bg-hover hover:text-foreground',
            )}
          >
            <Icon name={item.icon} className="h-5 w-5" />
            <span className="sr-only">{item.label}</span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-[var(--ink)] px-2 py-1 text-[11px] font-medium text-on-ink shadow-[var(--shadow-btn)] sm:group-hover:block"
            >
              {item.label}
            </span>
          </Link>
        )
      })}
    </nav>
  )
}
